import React, { useMemo } from 'react'
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  from,
  ApolloLink,
} from '@apollo/client'
import { RetryLink } from '@apollo/client/link/retry'

import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'
import {
  getGraphQLErrorFingerprint,
  getSentryNetworkErrorContext,
  getCurrentFirebaseUser,
  parseSeverityFromGqlErrors,
} from '@flock/utils'
import * as Sentry from '@sentry/gatsby'
// @ts-ignore
import { Severity } from '@flock/utils/src/__generated__/protobuf/ts/errors/errors'
import {
  SHARE_ACCREDITATION_V3_URL,
  SHARE_PORTFOLIO_PROPERTY_QUESTIONNAIRE_URL,
  SHARE_PROPERTY_QUESTIONNAIRE_V3_URL,
  SHARE_PERSONAL_INFO_V3_URL,
  SHARE_MORTGAGE_V3_URL,
} from '../constants'

type ConfiguredApolloProviderProps = {
  children: React.ReactNode
}

const CriticalOperationNames = [
  'GetPortfolio',
  'GetUpdates',
  'GetDocuments',
  'GetDocumentDownloadUrl',
  'GetOrdersByLegalEntity',
  'GetLegalEntity',
  'GetLegalEntitiesHoldings',
  'GetLegalEntitiesDistributions',
]

const RetryOperationNames = ['GetInvestorAccount']

const oneTimeLinkPaths = [
  SHARE_ACCREDITATION_V3_URL,
  SHARE_PORTFOLIO_PROPERTY_QUESTIONNAIRE_URL,
  SHARE_PROPERTY_QUESTIONNAIRE_V3_URL,
  SHARE_PERSONAL_INFO_V3_URL,
  SHARE_MORTGAGE_V3_URL,
]

const isOnOneTimeLinkPath = () =>
  typeof window !== 'undefined' &&
  oneTimeLinkPaths.some((path) => window.location.pathname.includes(path))

const ConfiguredApolloProvider = ({
  children,
}: ConfiguredApolloProviderProps) => {
  // The client must be created exactly once. Rebuilding it on re-render swaps
  // the client under ApolloProvider, which drops the cache and refires every
  // mounted query in the app.
  const client = useMemo(() => {
    const apolloLinks = []

    const retryLink = new RetryLink({
      delay: {
        initial: 300,
        max: Infinity,
        jitter: true,
      },
      attempts: {
        max: 5,
        retryIf: (_, operation) =>
          RetryOperationNames.some((retryName) =>
            operation.operationName.includes(retryName)
          ),
      },
    })
    apolloLinks.push(retryLink)

    const errorLink = onError((errResult) => {
      const { operation, graphQLErrors, networkError } = errResult
      const context = operation.getContext()
      const traceId = context.response?.headers?.get('X-Amzn-Trace-Id')
      const critical = CriticalOperationNames.includes(
        operation.operationName
      ).toString()

      const userEmail = getCurrentFirebaseUser()?.email

      const severity = parseSeverityFromGqlErrors(graphQLErrors)
      if (severity !== Severity.SEVERITY_IGNORE) {
        const error =
          networkError instanceof Error
            ? networkError
            : new Error(`GQL Operation failed: ${operation.operationName}`)
        Sentry.captureException(error, {
          fingerprint: getGraphQLErrorFingerprint(operation.operationName),
          tags: {
            userEmail,
            traceId,
            critical,
            operationName: operation.operationName,
          },
          extra: {
            variables: operation.variables,
            graphQLErrors,
            networkError: getSentryNetworkErrorContext(networkError),
          },
        })
      }
    })
    apolloLinks.push(errorLink)

    const authorizationLink = setContext(async (_, previousContext) => {
      // One-time-link pages are unauthenticated; send no Authorization header
      if (isOnOneTimeLinkPath()) {
        return {}
      }
      // If this token will expire in less than an hour, refresh the token
      const token = await getCurrentFirebaseUser()?.getIdToken()
      // When there's no signed-in user, send no Authorization header rather
      // than "Bearer undefined", which triggers avoidable auth errors/noise.
      if (!token) {
        return { headers: previousContext.headers }
      }
      return {
        headers: {
          ...previousContext.headers,
          Authorization: `Bearer ${token}`,
        },
      }
    })
    apolloLinks.push(authorizationLink)

    const uploadLink = createUploadLink({ uri: process.env.GATSBY_APOLLO_URL })
    apolloLinks.push(uploadLink)

    return new ApolloClient({
      link: from(apolloLinks as ApolloLink[]),
      cache: new InMemoryCache(),
    })
  }, [])

  return (
    <>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </>
  )
}

export default ConfiguredApolloProvider
